import React, {useEffect} from "react";
import {Tooltip} from "@chakra-ui/react";
import SmartIcon from "../../SmartIcon.tsx";
import {IconType} from "react-icons";
import {IStateEnum} from "../../../models/enums/state.enum.ts";
import AppSpinner from "../../AppSpinner.tsx";

type Props = {
  id: string;
  rotation: number;
  state: IStateEnum
  icon?: string | IconType;
  isInteractive: boolean;
  isHovered: boolean;
  onEnter: () => void;
  onLeave: () => void;
  onClick: () => void;
  tooltip?: string;
  content?: React.ReactNode;
}

const WorkflowStepRing: React.FC<Props> = (
  {
    id,
    rotation,
    state,
    icon,
    isInteractive,
    isHovered,
    onEnter,
    onLeave,
    onClick,
    tooltip,
    content,
  }: Props) => {
  const [isClicked, setIsClicked] = React.useState(false);

  useEffect(() => {
    if (isClicked) {
      const timer = setTimeout(() => setIsClicked(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isClicked]);

  const isDownloadable = isInteractive
    && rotation === 360
    && state === IStateEnum.DONE
    && !["arrow-prev", "subtitleTranslations", "audioDescriptions"].includes(id);

  const ringContent = isDownloadable ? (
    <span role="img" aria-label="Download translation">
      <SmartIcon name="download" className="w-5 h-5 fill-n-100" />
    </span>
  ) : icon ? (
    <SmartIcon
      name={icon}
      className={`w-5 h-5 dark:fill-n-200 ${icon === "arrow-next" ? "cursor-pointer" : ""}`}
    />
  ) : content;

  return (
    <button
      type="button"
      disabled={!isInteractive}
      aria-label={tooltip ?? (id === "arrow-prev" ? "Back" : undefined)}
      className={`relative w-10 h-10 flex items-center justify-center rounded-full transition-transform duration-100 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brandPurple-400 ${isInteractive ? "cursor-pointer active:scale-[0.96]" : "cursor-default opacity-70"}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={(e) => {
        e.stopPropagation();
        if (!isInteractive) return;
        if (isDownloadable) setIsClicked(true);
        onClick();
      }}
    >
      <span className="relative w-9 h-9 flex items-center justify-center">
        <span
          className="absolute inset-0 rounded-full"
          style={{
            background: `conic-gradient(
    ${state === IStateEnum.FAILED
              ? "color-mix(in srgb, var(--chakra-colors-bloodCrimson-400) 70%, transparent) 0deg 360deg"
              : `var(--chakra-colors-brandPurple-500) 0deg ${rotation}deg, var(--chakra-colors-n-400) ${rotation}deg 360deg`}
  )`,
            mask: isHovered
              ? "radial-gradient(farthest-side, transparent calc(100% - 5px), black calc(100% - 1px))"
              : "radial-gradient(farthest-side, transparent calc(100% - 3.5px), black calc(100% - 1px))",
            WebkitMask: isHovered
              ? "radial-gradient(farthest-side, transparent calc(100% - 5px), black calc(100% - 1px))"
              : "radial-gradient(farthest-side, transparent calc(100% - 3.5px), black calc(100% - 1px))",
            transition: "mask 0.2s ease, -webkit-mask 0.2s ease",
          }}
        />
        <span className="relative z-10 w-7 h-7 rounded-full bg-transparent flex items-center justify-center">
          {isClicked ? (
            <AppSpinner size="sm" />
          ) : tooltip ? (
            <Tooltip label={tooltip} className="dark:bg-n-500 dark:text-n-200 dark:px-3 dark:py-2 dark:rounded-md dark:text-sm">
              <span>{ringContent}</span>
            </Tooltip>
          ) : (
            <span>{ringContent}</span>
          )}
        </span>
      </span>
    </button>
  );
};

export default WorkflowStepRing;
